import { ChevronDownIcon, type LucideIcon } from 'lucide-react';
import { useEffect, useRef, type CSSProperties, type ReactNode } from 'react';
import { NavLink, useLocation } from 'react-router';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/utils';

export interface TabLink {
  to: string;
  label: ReactNode;
  icon?: LucideIcon;
  /** Small count or marker shown after the label. */
  badge?: ReactNode;
  end?: boolean;
}

/** A tab that opens a menu of links instead of navigating itself ("More"). */
export interface TabMenu {
  menu: true;
  label: ReactNode;
  icon?: LucideIcon;
  items: TabLink[];
}

export type TabItem = TabLink | TabMenu;

const isMenu = (t: TabItem): t is TabMenu => 'menu' in t && t.menu === true;
const matches = (pathname: string, to: string, end?: boolean) => (end ? pathname === to : pathname === to || pathname.startsWith(`${to}/`));

const tabClass = (isActive: boolean) =>
  cn(
    'group flex h-11 shrink-0 items-center gap-2 border-b-2 px-3 text-sm whitespace-nowrap transition-colors',
    'focus-visible:ring-ring/50 outline-none focus-visible:ring-[3px]',
    isActive ? 'border-primary text-foreground font-medium' : 'text-muted-foreground hover:text-foreground border-transparent',
  );

function TabBody({ icon: Icon, label, badge, active }: { icon?: LucideIcon; label: ReactNode; badge?: ReactNode; active: boolean }) {
  return (
    <>
      {Icon && <Icon className={cn('size-4 shrink-0 transition-colors', active ? 'text-foreground' : 'text-muted-foreground/70 group-hover:text-muted-foreground')} strokeWidth={1.75} aria-hidden />}
      {label}
      {badge !== undefined && badge !== null ? <span className={cn('min-w-5 px-1 text-center text-[11px] leading-5 tabular-nums', active ? 'bg-foreground/8 text-foreground' : 'bg-muted text-muted-foreground')}>{badge}</span> : null}
    </>
  );
}

/**
 * Underline tabs that scroll sideways on narrow screens instead of wrapping. A tab can carry an icon and a count,
 * and can also be a menu of secondary links: it reads as the active section's name while you are inside one, and
 * "More" otherwise. `accent` colours the active underline (a job's colour), so the strip belongs to what it navigates.
 *
 * The hairline lives on the wrapper and the scroller is pulled up 1px over it, so the active tab's 2px underline
 * overlaps the hairline without any element overflowing the scroll box. The scrollbar itself is hidden; edges fade
 * when there is more. The active tab is scrolled into view on route change, and the strip can stick under the header.
 */
export function TabStrip({ items, ariaLabel, sticky, bleed = true, accent, className }: { items: TabItem[]; ariaLabel: string; sticky?: boolean; /** Sticky strips bleed to the page edges by default; pass false to keep the content column's width. */ bleed?: boolean; accent?: string; className?: string }) {
  const scroller = useRef<HTMLElement>(null);
  const location = useLocation();
  const activeStyle = (isActive: boolean): CSSProperties | undefined => (isActive && accent ? { borderBottomColor: accent } : undefined);

  useEffect(() => {
    const el = scroller.current?.querySelector<HTMLElement>('[aria-current="page"]');
    if (el && scroller.current) {
      const s = scroller.current;
      const left = el.offsetLeft - s.clientWidth / 2 + el.clientWidth / 2;
      s.scrollTo({ left: Math.max(0, left), behavior: 'smooth' });
    }
  }, [location.pathname]);

  return (
    <div className={cn('border-b', sticky && 'bg-background/95 sticky top-14 z-20 backdrop-blur', sticky && bleed && '-mx-4 px-4 md:-mx-8 md:px-8', className)}>
      <nav ref={scroller} className="scrollbar-none scroll-fade-x -mb-px flex gap-1 overflow-x-auto" aria-label={ariaLabel}>
        {items.map((t, i) => {
          if (isMenu(t)) {
            if (t.items.length === 0) return null;
            const current = t.items.find((c) => matches(location.pathname, c.to, c.end));
            return (
              <DropdownMenu key={`menu-${i}`}>
                <DropdownMenuTrigger className={cn(tabClass(!!current), 'data-[state=open]:text-foreground')} style={activeStyle(!!current)} aria-current={current ? 'page' : undefined} aria-label={current ? `${String(t.label)}: ${typeof current.label === 'string' ? current.label : 'current section'}` : undefined}>
                  <TabBody icon={current?.icon ?? t.icon} label={current ? current.label : t.label} active={!!current} />
                  <ChevronDownIcon className="size-3.5 opacity-60" aria-hidden />
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start" className="min-w-48">
                  {t.items.map((c) => {
                    const on = matches(location.pathname, c.to, c.end);
                    return (
                      <DropdownMenuItem key={c.to} asChild>
                        <NavLink to={c.to} end={c.end} className={cn('flex items-center gap-2', on && 'bg-accent font-medium')}>
                          {c.icon && <c.icon className="text-muted-foreground size-4" aria-hidden />}
                          <span className="flex-1">{c.label}</span>
                          {c.badge !== undefined && c.badge !== null ? <span className="text-muted-foreground text-xs tabular-nums">{c.badge}</span> : null}
                        </NavLink>
                      </DropdownMenuItem>
                    );
                  })}
                </DropdownMenuContent>
              </DropdownMenu>
            );
          }
          return (
            <NavLink key={t.to} to={t.to} end={t.end} className={({ isActive }) => tabClass(isActive)} style={({ isActive }) => activeStyle(isActive)}>
              {({ isActive }) => <TabBody icon={t.icon} label={t.label} badge={t.badge} active={isActive} />}
            </NavLink>
          );
        })}
      </nav>
    </div>
  );
}
